import { z } from "zod";

/**
 * Document schema (SPEC.md §3). Plain JSON, validated on both client and server.
 * Nodes live in one flat id-keyed map; containers (pages, groups) hold ordered child ids.
 * Bump SCHEMA_VERSION and add a step to migrations.ts for any breaking change.
 */
export const SCHEMA_VERSION = 7;

export const Id = z.string().min(1).max(64);
export type Id = z.infer<typeof Id>;

/** Always 8-digit #RRGGBBAA so alpha is explicit and comparisons are cheap. */
export const Color = z.string().regex(/^#[0-9a-f]{8}$/, "colour must be #rrggbbaa (lowercase)");
export type Color = z.infer<typeof Color>;

/** 2x3 affine [a, b, c, d, e, f]: x' = a·x + c·y + e, y' = b·x + d·y + f (canvas/SVG order). */
export const Matrix = z.tuple([z.number(), z.number(), z.number(), z.number(), z.number(), z.number()]);
export type Matrix = z.infer<typeof Matrix>;
export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** Blend modes Skia implements natively (also valid for individual layer styles). */
export const BASE_BLEND_MODES = [
  "normal", "multiply", "screen", "overlay", "darken", "lighten", "color-dodge", "color-burn",
  "hard-light", "soft-light", "difference", "exclusion", "hue", "saturation", "color", "luminosity",
] as const;
/** The rest of Photoshop's set; the engine implements these with SkSL blenders. */
export const EXTRA_BLEND_MODES = [
  "linear-burn", "linear-dodge", "vivid-light", "linear-light", "pin-light", "hard-mix", "divide", "subtract", "darker-color", "lighter-color",
] as const;
export const BLEND_MODES = [...BASE_BLEND_MODES, ...EXTRA_BLEND_MODES] as const;
export const BlendMode = z.enum(BLEND_MODES);
export type BlendMode = z.infer<typeof BlendMode>;
/** Photoshop's menu grouping (normal · darken · lighten · contrast · inversion · component). */
export const BLEND_MODE_GROUPS: readonly (readonly BlendMode[])[] = [
  ["normal"],
  ["darken", "multiply", "color-burn", "linear-burn", "darker-color"],
  ["lighten", "screen", "color-dodge", "linear-dodge", "lighter-color"],
  ["overlay", "soft-light", "hard-light", "vivid-light", "linear-light", "pin-light", "hard-mix"],
  ["difference", "exclusion", "subtract", "divide"],
  ["hue", "saturation", "color", "luminosity"],
];
export const StyleBlendMode = z.enum(BASE_BLEND_MODES);
export type StyleBlendMode = z.infer<typeof StyleBlendMode>;

export const SolidPaint = z.object({ type: z.literal("solid"), color: Color });
export const GradientStop = z.object({ offset: z.number().min(0).max(1), color: Color });
export type GradientStop = z.infer<typeof GradientStop>;
const Stops = z.array(GradientStop).min(2).max(16);
const BoxPoint = z.tuple([z.number(), z.number()]);
/**
 * CSS-style: `angle` in degrees (0 = left→right, 90 = top→bottom) across the node's box.
 * Optional `start`/`end` (box fractions, 0..1 = the box) pin the gradient line exactly, as the
 * gradient tool sets them; when present they win over `angle` (which is kept roughly in sync).
 */
export const LinearPaint = z.object({ type: z.literal("linear"), stops: Stops, angle: z.number(), start: BoxPoint.optional(), end: BoxPoint.optional() });
/** Centre (cx, cy) in 0..1 of the box; radius in units of half the box diagonal. */
export const RadialPaint = z.object({ type: z.literal("radial"), stops: Stops, cx: z.number(), cy: z.number(), radius: z.number().positive().max(10) });
/** Sweep around (cx, cy); `angle` in degrees is where offset 0 starts (0 = pointing right, clockwise). */
export const ConicPaint = z.object({ type: z.literal("conic"), stops: Stops, cx: z.number(), cy: z.number(), angle: z.number() });
/** Square "diamond" rings around (cx, cy); radius as for radial, `angle` rotates the diamond. */
export const DiamondPaint = z.object({ type: z.literal("diamond"), stops: Stops, cx: z.number(), cy: z.number(), radius: z.number().positive().max(10), angle: z.number() });
export const Paint = z.discriminatedUnion("type", [SolidPaint, LinearPaint, RadialPaint, ConicPaint, DiamondPaint]);
export type Paint = z.infer<typeof Paint>;
export type LinearPaint = z.infer<typeof LinearPaint>;
export type RadialPaint = z.infer<typeof RadialPaint>;
export type ConicPaint = z.infer<typeof ConicPaint>;
export type DiamondPaint = z.infer<typeof DiamondPaint>;
export type GradientPaint = Exclude<Paint, { type: "solid" }>;

/**
 * Effects (SPEC.md §3), applied in the parent's coordinate space so a shadow keeps pointing
 * the same way on the page when the element is rotated (as in Canva).
 */
const Pct0 = z.number().min(0).max(100);
const Toggle = { enabled: z.boolean().optional() }; // absent = on
const Dist = z.number().min(0).max(500);
const Angle = z.number().min(-360).max(360);
export const ShadowEffect = z.object({ type: z.literal("shadow"), color: Color, x: z.number(), y: z.number(), blur: z.number().min(0).max(500), ...Toggle });
export const GlowEffect = z.object({ type: z.literal("glow"), color: Color, size: z.number().min(0).max(500), ...Toggle });
export const BlurEffect = z.object({ type: z.literal("blur"), radius: z.number().min(0).max(500), ...Toggle });
/*
 * Photoshop-style layer styles. They act on the element's silhouette (its alpha) in the parent's
 * space like the effects above; colours carry the style's opacity in their alpha.
 */
/** Outline traced around the silhouette (unlike a border, it follows text, photos and groups). */
export const StrokeEffect = z.object({ type: z.literal("stroke"), position: z.enum(["inside", "center", "outside"]), width: Dist, paint: Paint, ...Toggle });
/** `choke` (0..100 %) hardens the edge before blurring. */
export const InnerShadowEffect = z.object({ type: z.literal("innerShadow"), color: Color, x: z.number(), y: z.number(), blur: Dist, choke: Pct0, blendMode: StyleBlendMode, ...Toggle });
export const InnerGlowEffect = z.object({ type: z.literal("innerGlow"), color: Color, size: Dist, source: z.enum(["center", "edge"]), choke: Pct0, blendMode: StyleBlendMode, ...Toggle });
/**
 * Lit relief. `depth` is a percentage (1..1000), `size`/`soften` px, light `angle` (0 = from the
 * right, 90 = from the top, Photoshop's convention) and `altitude` 0..90°.
 */
export const BevelEffect = z.object({
  type: z.literal("bevel"),
  style: z.enum(["inner", "outer", "emboss"]),
  direction: z.enum(["up", "down"]),
  depth: z.number().min(1).max(1000),
  size: Dist,
  soften: z.number().min(0).max(50),
  angle: Angle,
  altitude: z.number().min(0).max(90),
  highlight: Color,
  shadow: Color,
  ...Toggle,
});
export const ColorOverlayEffect = z.object({ type: z.literal("colorOverlay"), color: Color, blendMode: StyleBlendMode, ...Toggle });
/** Paint laid over the element's box, clipped to its silhouette. */
export const GradientOverlayEffect = z.object({ type: z.literal("gradientOverlay"), paint: Paint, opacity: z.number().min(0).max(1), blendMode: StyleBlendMode, ...Toggle });
/** An image asset tiled over the element at `scale` (1 = the image's pixel size). */
export const PatternOverlayEffect = z.object({ type: z.literal("patternOverlay"), assetId: Id, scale: z.number().min(0.01).max(100), opacity: z.number().min(0).max(1), blendMode: StyleBlendMode, ...Toggle });
/** Interior silky shading: the silhouette offset both ways along `angle`, differenced and blurred. */
export const SatinEffect = z.object({ type: z.literal("satin"), color: Color, angle: Angle, distance: Dist, size: Dist, invert: z.boolean(), blendMode: StyleBlendMode, ...Toggle });
export const Effect = z.discriminatedUnion("type", [
  ShadowEffect, GlowEffect, BlurEffect, StrokeEffect, InnerShadowEffect, InnerGlowEffect, BevelEffect, ColorOverlayEffect, GradientOverlayEffect, PatternOverlayEffect, SatinEffect,
]);
export type Effect = z.infer<typeof Effect>;
export type EffectType = Effect["type"];
export type EffectOf<T extends EffectType> = Extract<Effect, { type: T }>;

export const Stroke = z.object({
  paint: Paint,
  width: z.number().min(0).max(10_000),
  align: z.enum(["center", "inside", "outside"]),
  dash: z.array(z.number().min(0)).max(16),
  cap: z.enum(["butt", "round", "square"]),
  join: z.enum(["miter", "round", "bevel"]),
});
export type Stroke = z.infer<typeof Stroke>;

const Size = z.number().min(0).max(1_000_000);

const Pt2 = z.tuple([z.number(), z.number()]);

/**
 * Non-destructive 4-corner distortion (SPEC.md §5): the node's box (0,0,w,h) is mapped
 * projectively onto `corners` [top-left, top-right, bottom-right, bottom-left], in the
 * node's local units. Covers both perspective and free distort.
 */
export const PerspectiveWarp = z.object({
  type: z.literal("perspective"),
  corners: z.tuple([Pt2, Pt2, Pt2, Pt2]),
});
export type PerspectiveWarp = z.infer<typeof PerspectiveWarp>;

/** Largest mesh grid (patches per side). */
export const MESH_MAX = 8;
export const meshPointCount = (cols: number, rows: number) => (3 * cols + 1) * (3 * rows + 1);

/**
 * Photoshop-style mesh warp (SPEC.md §5): `cols` × `rows` bicubic tensor-product Bézier
 * patches laid over the node's unwarped box. `points` is the shared control lattice,
 * (3·cols+1) × (3·rows+1) points row by row (x fastest), in the node's local units.
 * Lattice points with both indices divisible by 3 are anchors, the rest of each patch edge
 * are tangent handles, and the 4 points inside each patch are interior controls.
 */
export const MeshWarpParams = z
  .object({
    type: z.literal("mesh"),
    cols: z.int().min(1).max(MESH_MAX),
    rows: z.int().min(1).max(MESH_MAX),
    points: z.array(Pt2).max(meshPointCount(MESH_MAX, MESH_MAX)),
  })
  .refine((w) => w.points.length === meshPointCount(w.cols, w.rows), "mesh point count must match its grid size");
export type MeshWarpParams = z.infer<typeof MeshWarpParams>;

/** Most pins one puppet warp holds. */
export const PUPPET_MAX_PINS = 64;

/**
 * A puppet pin: `rest` is where it was placed on the unwarped node, `pos` where it's been
 * dragged to (node-local units). `rotation` (degrees) holds the pin's neighbourhood at a fixed
 * angle; null lets the solver turn it. Higher `depth` draws on top where folded parts overlap.
 */
export const PuppetPin = z.object({
  rest: Pt2,
  pos: Pt2,
  rotation: z.number().min(-3600).max(3600).nullable(),
  depth: z.int().min(-100).max(100),
});
export type PuppetPin = z.infer<typeof PuppetPin>;

/**
 * Photoshop-style puppet warp (SPEC.md §5): the node's silhouette (a photo's opaque pixels,
 * a shape's or text's outline), grown by `expansion` local units, is triangulated at
 * `density` and bent as-rigidly-as-possible around the pins. `mode`: rigid keeps every
 * triangle's size, distort lets them scale, normal is in between. Added without a schema bump.
 */
export const PuppetWarp = z.object({
  type: z.literal("puppet"),
  density: z.enum(["fewer", "normal", "more"]),
  mode: z.enum(["rigid", "normal", "distort"]),
  expansion: z.number().min(-500).max(500),
  pins: z.array(PuppetPin).max(PUPPET_MAX_PINS),
});
export type PuppetWarp = z.infer<typeof PuppetWarp>;

export const Warp = z.discriminatedUnion("type", [PerspectiveWarp, MeshWarpParams, PuppetWarp]);
export type Warp = z.infer<typeof Warp>;

/**
 * Liquify (SPEC.md §5): a displacement field over a region of the node's local space, applied
 * after any warp. Output point p shows the content at p + d(p). `cols` × `rows` samples sit at
 * cell centres of the region; displacements are integers × `step` (local units), delta- and
 * run-length-coded as zig-zag varints in base64 (dx plane, then dy). `freeze` is the brush's
 * protection mask (0..255 per sample, same coding). Codec and maths: @canvas/geometry liquify.ts.
 * Resizing a liquified node scales its transform, so the field never needs resampling.
 */
export const LIQUIFY_MAX_CELLS = 384;
export const Liquify = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
  cols: z.int().min(2).max(LIQUIFY_MAX_CELLS),
  rows: z.int().min(2).max(LIQUIFY_MAX_CELLS),
  step: z.number().positive(),
  data: z.string().max(2_000_000).regex(/^[A-Za-z0-9+/]*={0,2}$/),
  freeze: z.string().max(1_000_000).regex(/^[A-Za-z0-9+/]*={0,2}$/).nullable(),
});
export type Liquify = z.infer<typeof Liquify>;

/** Raster tiles are always this many pixels square (SPEC.md §4.3). */
export const RASTER_TILE = 256;
/** Largest raster layer side, and the pixel budget shared with images. */
export const RASTER_MAX_SIDE = 16_384;
/**
 * A raster layer's pixel content: the asset id (SHA-256) of its packed tile blob, or
 * `local:<id>` for pixels painted since the last save, held by the editor's tile store.
 * Saved documents only ever contain asset ids (the client uploads before saving).
 */
export const RasterRef = z.string().regex(/^([0-9a-f]{64}|local:[A-Za-z0-9]{1,40})$/, "raster ref must be an asset id or local:<id>");
export const isLocalRasterRef = (ref: string | null): ref is `local:${string}` => !!ref && ref.startsWith("local:");

/**
 * Layer mask (Photoshop-style, SPEC.md §3). Every part is optional and they multiply:
 *  - `raster`: painted pixels in the raster-layer tile format (see packages/engine/RASTER.md),
 *    covering (0, 0, width, height) of mask space. A pixel's ALPHA is how much it HIDES, so an
 *    empty mask (no tiles) reveals everything and painting only stores the hidden parts.
 *  - `vector`: a path in mask space (reveals inside), or another node whose silhouette reveals
 *    (e.g. text: "photo inside letters"). A node used as a mask isn't drawn on its own.
 *  - `gradient`: a linear fade in mask space, revealed at `start` → hidden at `end`.
 * `transform` maps mask space into the node's local space when `linked` (the mask moves with
 * the layer), or into the parent's space when unlinked (the mask stays put on the page).
 * The result is feathered (`feather`, parent units), optionally inverted, then `density` scales
 * how much it hides (0 = no effect). `enabled: false` keeps it but shows the layer unmasked.
 */
export const MaskRaster = z.object({
  width: Size,
  height: Size,
  pixelWidth: z.int().min(1).max(RASTER_MAX_SIDE),
  pixelHeight: z.int().min(1).max(RASTER_MAX_SIDE),
  tileSize: z.literal(RASTER_TILE),
  /** null = nothing painted (reveals all). */
  tiles: RasterRef.nullable(),
  /** Optional: what made it ("subject" = Remove background; "Restore background" deletes it). */
  origin: z.enum(["subject", "selection"]).optional(),
});
export type MaskRaster = z.infer<typeof MaskRaster>;
export const MaskVector = z.discriminatedUnion("type", [
  z.object({ type: z.literal("path"), cmds: z.array(z.number()).max(400_000).refine((c) => validPathCmds(c), "malformed path commands"), fillRule: z.enum(["nonzero", "evenodd"]) }),
  z.object({ type: z.literal("node"), nodeId: Id }),
]);
export type MaskVector = z.infer<typeof MaskVector>;
export const MaskGradient = z.object({ start: Pt2, end: Pt2 });
export type MaskGradient = z.infer<typeof MaskGradient>;
export const Mask = z.object({
  transform: Matrix,
  raster: MaskRaster.nullable().optional(),
  vector: MaskVector.nullable().optional(),
  gradient: MaskGradient.nullable().optional(),
  density: z.number().min(0).max(1),
  feather: z.number().min(0).max(1000),
  invert: z.boolean(),
  enabled: z.boolean(),
  linked: z.boolean(),
});
export type Mask = z.infer<typeof Mask>;

const NodeBase = z.object({
  id: Id,
  name: z.string().max(200),
  /** Page or group id. */
  parentId: Id,
  transform: Matrix,
  opacity: z.number().min(0).max(1),
  blendMode: BlendMode,
  visible: z.boolean(),
  locked: z.boolean(),
  effects: z.array(Effect).max(16),
  warp: Warp.nullable(),
  /** Optional (added without a schema bump): absent or null = not liquified. */
  liquify: Liquify.nullable().optional(),
  /** Optional layer mask (absent or null = none). */
  mask: Mask.nullable().optional(),
  /** Clipping mask: shown only where the nearest non-clipping sibling below it is (absent = false). */
  clip: z.boolean().optional(),
});

const Styled = { fills: z.array(Paint).max(16), strokes: z.array(Stroke).max(16) };

export const RectNode = NodeBase.extend({ type: z.literal("rect"), width: Size, height: Size, cornerRadius: z.number().min(0), ...Styled });
export const EllipseNode = NodeBase.extend({ type: z.literal("ellipse"), width: Size, height: Size, ...Styled });
export const PolygonNode = NodeBase.extend({ type: z.literal("polygon"), width: Size, height: Size, sides: z.int().min(3).max(64), cornerRadius: z.number().min(0), ...Styled });
export const StarNode = NodeBase.extend({
  type: z.literal("star"),
  width: Size,
  height: Size,
  points: z.int().min(3).max(64),
  innerRatio: z.number().min(0.05).max(0.95),
  ...Styled,
});
export const LineEnd = z.enum(["none", "arrow", "triangle", "circle", "square", "bar"]);
export type LineEnd = z.infer<typeof LineEnd>;
/**
 * Runs from local (0,0) to (width, height). Straight lines keep height 0 and rotate via the
 * transform; elbow and curved connectors use height for the vertical offset.
 */
export const LineNode = NodeBase.extend({
  type: z.literal("line"),
  width: z.number().min(-1_000_000).max(1_000_000),
  height: z.number().min(-1_000_000).max(1_000_000),
  routing: z.enum(["straight", "elbow", "curved"]),
  strokes: z.array(Stroke).max(16),
  start: LineEnd,
  end: LineEnd,
});
/** Photo adjustments, each -100..100 with 0 = unchanged (Canva's "Adjust" panel). */
export const ADJUSTMENTS = ["brightness", "contrast", "saturation", "vibrance", "temperature", "tint", "highlights", "shadows", "whites", "blacks", "vignette"] as const;
export type AdjustmentKey = (typeof ADJUSTMENTS)[number];
export const Adjustments = z.object(Object.fromEntries(ADJUSTMENTS.map((k) => [k, z.number().min(-100).max(100)])) as Record<AdjustmentKey, z.ZodNumber>);
export type Adjustments = z.infer<typeof Adjustments>;
export const NO_ADJUSTMENTS: Adjustments = Object.fromEntries(ADJUSTMENTS.map((k) => [k, 0])) as Adjustments;

/** Tone curve: control points (input, output) in 0..1, sorted by input; absent = straight line. */
export const CurvePoints = z.array(z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)])).min(2).max(16);
export type CurvePoints = z.infer<typeof CurvePoints>;
/** Per-channel curves ("rgb" = composite); a missing channel is the identity. */
export const Curves = z.object({ rgb: CurvePoints.optional(), r: CurvePoints.optional(), g: CurvePoints.optional(), b: CurvePoints.optional() });
export type Curves = z.infer<typeof Curves>;
export const CURVE_CHANNELS = ["rgb", "r", "g", "b"] as const;
export type CurveChannel = (typeof CURVE_CHANNELS)[number];
/** Photoshop-style levels for one channel; values 0..1, gamma 1 = linear (>1 brightens midtones). */
export const LevelsChannel = z.object({
  inBlack: z.number().min(0).max(1),
  inWhite: z.number().min(0).max(1),
  gamma: z.number().min(0.1).max(9.99),
  outBlack: z.number().min(0).max(1),
  outWhite: z.number().min(0).max(1),
});
export type LevelsChannel = z.infer<typeof LevelsChannel>;
export const NEUTRAL_LEVELS: LevelsChannel = { inBlack: 0, inWhite: 1, gamma: 1, outBlack: 0, outWhite: 1 };
export const Levels = z.object({ rgb: LevelsChannel.optional(), r: LevelsChannel.optional(), g: LevelsChannel.optional(), b: LevelsChannel.optional() });
export type Levels = z.infer<typeof Levels>;
/** Hue ranges for the HSL mixer, in hue order (Lightroom's eight). */
export const HSL_RANGES = ["red", "orange", "yellow", "green", "aqua", "blue", "purple", "magenta"] as const;
export type HslRange = (typeof HSL_RANGES)[number];
/** Shifts for one hue range, each -100..100 with 0 = unchanged. */
export const HslShift = z.object({ hue: z.number().min(-100).max(100), saturation: z.number().min(-100).max(100), lightness: z.number().min(-100).max(100) });
export type HslShift = z.infer<typeof HslShift>;
export const Hsl = z.object(Object.fromEntries(HSL_RANGES.map((k) => [k, HslShift.optional()])) as Record<HslRange, z.ZodOptional<typeof HslShift>>);
export type Hsl = z.infer<typeof Hsl>;
/**
 * Non-destructive filter stack (Photoshop-style "smart filters"), applied in order after the
 * adjustments above. Distances are in the image box's own units (like effects), so a filter
 * looks the same at any zoom and in exports. Ranges/defaults for the UI live in filters.ts.
 */
const Ang = z.number().min(-180).max(180);
const Unit = z.number().min(0).max(1);
const FilterBase = { id: Id, enabled: z.boolean() };
const F = <T extends string, P extends z.ZodRawShape>(type: T, params: P) => z.object({ ...FilterBase, type: z.literal(type), params: z.object(params) });
export const FilterLayer = z.discriminatedUnion("type", [
  F("gaussianBlur", { radius: z.number().min(0).max(250) }),
  F("motionBlur", { angle: Ang, distance: z.number().min(0).max(1000) }),
  F("radialBlur", { mode: z.enum(["spin", "zoom"]), amount: z.number().min(0).max(100), cx: Unit, cy: Unit }),
  F("lensBlur", { radius: z.number().min(0).max(100), brightness: z.number().min(0).max(100), threshold: z.int().min(0).max(255) }),
  F("tiltShift", { blur: z.number().min(0).max(100), y: Unit, angle: z.number().min(-90).max(90), focus: Unit, transition: Unit }),
  F("unsharpMask", { amount: z.number().min(0).max(500), radius: z.number().min(0.1).max(250), threshold: z.int().min(0).max(255) }),
  F("highPass", { radius: z.number().min(0.1).max(250) }),
  F("addNoise", { amount: z.number().min(0).max(100), mono: z.boolean() }),
  F("reduceNoise", { strength: z.number().min(0).max(10), preserveDetails: z.number().min(0).max(100) }),
  F("emboss", { angle: Ang, height: z.number().min(1).max(100), amount: z.number().min(1).max(500) }),
  F("findEdges", {}),
  F("pixelate", { cell: z.number().min(2).max(200) }),
  F("halftone", { size: z.number().min(2).max(100), angle: Ang }),
  F("oilPaint", { radius: z.number().min(1).max(50) }),
  F("invert", {}),
  F("posterize", { levels: z.int().min(2).max(255) }),
  F("threshold", { level: z.int().min(1).max(255) }),
  F("gradientMap", { stops: z.array(GradientStop).min(2).max(16) }),
  F("blackWhite", Object.fromEntries((["reds", "yellows", "greens", "cyans", "blues", "magentas"] as const).map((k) => [k, z.number().min(-200).max(300)])) as Record<"reds" | "yellows" | "greens" | "cyans" | "blues" | "magentas", z.ZodNumber>),
  F("photoFilter", { color: Color, density: z.number().min(0).max(100), preserveLuminosity: z.boolean() }),
]);
export type FilterLayer = z.infer<typeof FilterLayer>;
export type FilterType = FilterLayer["type"];
export type FilterOf<T extends FilterType> = Extract<FilterLayer, { type: T }>;

export const ImageNode = NodeBase.extend({
  type: z.literal("image"),
  width: Size,
  height: Size,
  assetId: Id,
  /**
   * Visible part of the source, normalised 0..1. `angle` (degrees, optional) straightens the
   * photo: it is rotated about the box centre inside the crop, after the crop places it.
   */
  crop: z.object({ x: z.number(), y: z.number(), w: z.number().positive(), h: z.number().positive(), angle: z.number().min(-45).max(45).optional() }),
  cornerRadius: z.number().min(0),
  strokes: z.array(Stroke).max(16),
  adjust: Adjustments,
  /** Named look (see the engine's filter presets) blended in at `intensity` 0..1. */
  filter: z.object({ id: z.string().max(40), intensity: z.number().min(0).max(1) }).nullable(),
  duotone: z.object({ shadow: Color, highlight: Color }).nullable(),
  // Pro colour tools (Advanced sidebar). All optional: absent means neutral, so older documents stay valid.
  curves: Curves.optional(),
  levels: Levels.optional(),
  hsl: Hsl.optional(),
  /** Bundled 3D LUT preset (engine's LUT_PRESETS) blended in at `intensity` 0..1. */
  lut: z.object({ id: z.string().max(40), intensity: z.number().min(0).max(1) }).optional(),
  /** Unsharp mask amount 0..100. */
  sharpen: z.number().min(0).max(100).optional(),
  /** Film grain amount 0..100. */
  grain: z.number().min(0).max(100).optional(),
  /** Optional so documents saved before filters existed stay valid (no schema version bump). */
  filters: z.array(FilterLayer).max(32).optional(),
  /** Set by "Remove background": the photo before the cut-out, so "Restore background" works. */
  originalAssetId: Id.optional(),
  /**
   * Retouch strokes (clone, heal, dodge…) and brush strokes painted on the photo: refs to tile
   * blobs in the raster-layer format (packages/engine/RASTER.md), in the photo's SOURCE-pixel
   * space (the whole uncropped asset at its working size, see the engine's `photoPixelSize`), so
   * crop, mask, warp and liquify carry them along. Render order: source → retouch → colour and
   * filters → paint → warp/liquify → mask/crop → effects. Absent/null = nothing painted.
   */
  retouchTiles: RasterRef.nullable().optional(),
  paintTiles: RasterRef.nullable().optional(),
  /** Hide the retouch / paint strokes without deleting them. */
  retouchHidden: z.boolean().optional(),
  paintHidden: z.boolean().optional(),
});
export const GroupNode = NodeBase.extend({ type: z.literal("group"), children: z.array(Id) });

/** True if `cmds` is a well-formed path command list (CanvasKit MakeFromCmds layout). */
export function validPathCmds(cmds: number[]): boolean {
  const ARGS = [2, 2, 4, 5, 6, 0]; // move, line, quad, conic, cubic, close
  let i = 0;
  while (i < cmds.length) {
    const v = cmds[i++];
    if (!Number.isInteger(v) || v < 0 || v > 5) return false;
    i += ARGS[v];
    if (i > cmds.length) return false;
  }
  return cmds.length === 0 || cmds[0] === 0;
}

/** Free-form vector path (SVG import, pen tool). Coordinates are local, spanning (0,0,width,height). */
export const PathNode = NodeBase.extend({
  type: z.literal("path"),
  width: Size,
  height: Size,
  cmds: z.array(z.number()).max(400_000).refine(validPathCmds, "malformed path commands"),
  fillRule: z.enum(["nonzero", "evenodd"]),
  ...Styled,
});
export type PathNode = z.infer<typeof PathNode>;

/**
 * Frame (Canva "Frames"): a container whose children (usually one photo) are clipped to a
 * shape. `cmds` is the clip outline normalised to a 1×1 box, scaled to width × height.
 * `fills` show while the frame is empty.
 */
export const FrameNode = NodeBase.extend({
  type: z.literal("frame"),
  width: Size,
  height: Size,
  cmds: z.array(z.number()).max(100_000).refine(validPathCmds, "malformed path commands"),
  cornerRadius: z.number().min(0),
  children: z.array(Id),
  fills: z.array(Paint).max(16),
  strokes: z.array(Stroke).max(16),
});
export type FrameNode = z.infer<typeof FrameNode>;

/** Character style of a run of text. */
export const TextStyle = z.object({
  fontFamily: z.string().min(1).max(80),
  fontWeight: z.int().min(100).max(900),
  italic: z.boolean(),
  fontSize: z.number().min(1).max(4000),
  /** Solid colour or gradient text. */
  fill: Paint,
  /** Thousandths of an em, like Canva's letter spacing (-200..800). */
  letterSpacing: z.number().min(-1000).max(2000),
  underline: z.boolean(),
  strikethrough: z.boolean(),
  uppercase: z.boolean(),
});
export type TextStyle = z.infer<typeof TextStyle>;
export const TextSpan = z.object({ text: z.string().max(200_000), style: TextStyle });
export type TextSpan = z.infer<typeof TextSpan>;

/**
 * Canva's text "Effects → Style". Distances/sizes are 0..100 and scale with the font size so
 * an effect looks the same at any text size.
 */
const Dir = z.number().min(-360).max(360);
const Pct = z.number().min(0).max(100);
export const TextEffect = z.discriminatedUnion("type", [
  z.object({ type: z.literal("none") }),
  z.object({ type: z.literal("shadow"), offset: Pct, direction: Dir, blur: Pct, color: Color }),
  z.object({ type: z.literal("lift"), intensity: Pct }),
  z.object({ type: z.literal("hollow"), thickness: Pct }),
  z.object({ type: z.literal("splice"), thickness: Pct, offset: Pct, direction: Dir, color: Color }),
  z.object({ type: z.literal("outline"), thickness: Pct, color: Color }),
  z.object({ type: z.literal("echo"), offset: Pct, direction: Dir, color: Color }),
  z.object({ type: z.literal("glitch"), offset: Pct, direction: Dir, palette: z.enum(["blue-red", "cyan-magenta"]) }),
  z.object({ type: z.literal("neon"), intensity: Pct }),
  z.object({ type: z.literal("background"), roundness: Pct, spread: Pct, color: Color }),
]);
export type TextEffect = z.infer<typeof TextEffect>;

export const TextNode = NodeBase.extend({
  type: z.literal("text"),
  width: Size,
  /** Derived from layout by the engine (kept in the document so bounds work without fonts). */
  height: Size,
  /** auto-width grows with the text; fixed-width wraps at `width`. */
  sizing: z.enum(["auto-width", "fixed-width"]),
  spans: z.array(TextSpan).min(1).max(10_000),
  align: z.enum(["left", "center", "right", "justify"]),
  /** Multiple of the font size. */
  lineHeight: z.number().min(0.5).max(5),
  effect: TextEffect,
  /** Curved text: -100 (arch down) … 100 (arch up); 0 = straight. */
  curve: z.number().min(-100).max(100),
  /** Bulleted or numbered paragraphs. */
  list: z.enum(["none", "bullet", "number"]),
});
export type TextNode = z.infer<typeof TextNode>;

/** A photo's own fields, kept on a raster layer made from it (see `RasterNode.rasterizedFrom`). */
export const RasterSource = ImageNode.omit({ type: true, name: true, parentId: true, transform: true, opacity: true, blendMode: true, visible: true, locked: true, effects: true, warp: true, mask: true, clip: true, width: true, height: true });
export type RasterSource = z.infer<typeof RasterSource>;

/**
 * Tiled pixel layer (SPEC.md §3 "Raster", §4.3). The bitmap is pixelWidth × pixelHeight,
 * displayed stretched over the node's width × height box. Pixels never live in the document:
 * `tiles` points at a packed blob of 256² tiles (see packages/engine/RASTER.md).
 */
export const RasterNode = NodeBase.extend({
  type: z.literal("raster"),
  width: Size,
  height: Size,
  pixelWidth: z.int().min(1).max(RASTER_MAX_SIDE),
  pixelHeight: z.int().min(1).max(RASTER_MAX_SIDE),
  tileSize: z.literal(RASTER_TILE),
  /** null = fully transparent. */
  tiles: RasterRef.nullable(),
  /**
   * @deprecated v6 only: a photo's retouch layer (the image node it sat above, grouped with it).
   * Since v7 retouch strokes live on the photo (`ImageNode.retouchTiles`); the editor folds
   * these layers into their photo when it opens an older document.
   */
  retouchOf: Id.optional(),
  /** @deprecated v6 only: a photo's paint layer; since v7 see `ImageNode.paintTiles`. */
  paintOf: Id.optional(),
  /**
   * Set by "Rasterise": the photo this layer was made from (its id and photo-only fields:
   * asset, crop, adjustments, filters, liquify…), so "Revert to original photo" can restore it
   * exactly. Layout, effects, warp and mask stay live on the layer and carry back over.
   */
  rasterizedFrom: RasterSource.optional(),
});
export type RasterNode = z.infer<typeof RasterNode>;

/**
 * Adjustment layer (Photoshop): recolours everything drawn beneath it inside its parent (the
 * page, or its group, which then composites in isolation), through its own mask and opacity.
 * Parameters reuse the image colour tools' shapes (levels, curves, HSL) and the filter stack's
 * colour filters. `width × height` is only its box for selection and mask painting; it
 * affects its whole parent regardless.
 */
const Signed = z.number().min(-100).max(100);
const BalanceTone = z.object({ cyanRed: Signed, magentaGreen: Signed, yellowBlue: Signed });
const A = <K extends string, P extends z.ZodRawShape>(kind: K, params: P) => z.object({ kind: z.literal(kind), ...params });
export const AdjustmentParams = z.discriminatedUnion("kind", [
  A("brightnessContrast", { brightness: Signed, contrast: Signed }),
  A("levels", { levels: Levels }),
  A("curves", { curves: Curves }),
  /** Hue/saturation: a master shift plus the image tools' per-range HSL mixer. */
  A("hsl", { master: HslShift, hsl: Hsl }),
  A("colorBalance", { shadows: BalanceTone, midtones: BalanceTone, highlights: BalanceTone, preserveLuminosity: z.boolean() }),
  A("blackWhite", { ...(Object.fromEntries((["reds", "yellows", "greens", "cyans", "blues", "magentas"] as const).map((k) => [k, z.number().min(-200).max(300)])) as Record<"reds" | "yellows" | "greens" | "cyans" | "blues" | "magentas", z.ZodNumber>), tint: Color.nullable() }),
  A("photoFilter", { color: Color, density: z.number().min(0).max(100), preserveLuminosity: z.boolean() }),
  A("invert", {}),
  A("posterize", { levels: z.int().min(2).max(255) }),
  A("threshold", { level: z.int().min(1).max(255) }),
  A("gradientMap", { stops: z.array(GradientStop).min(2).max(16), reverse: z.boolean() }),
  A("vibrance", { vibrance: Signed, saturation: Signed }),
  /** `exposure` in stops, `offset` added before gamma, `gamma` 1 = linear. */
  A("exposure", { exposure: z.number().min(-20).max(20), offset: z.number().min(-0.5).max(0.5), gamma: z.number().min(0.01).max(9.99) }),
]);
export type AdjustmentParams = z.infer<typeof AdjustmentParams>;
export type AdjustmentKind = AdjustmentParams["kind"];
export type AdjustmentOf<K extends AdjustmentKind> = Extract<AdjustmentParams, { kind: K }>;
export const AdjustmentNode = NodeBase.extend({ type: z.literal("adjustment"), width: Size, height: Size, adjustment: AdjustmentParams });
export type AdjustmentNode = z.infer<typeof AdjustmentNode>;

export const Node = z.discriminatedUnion("type", [RectNode, EllipseNode, PolygonNode, StarNode, LineNode, ImageNode, GroupNode, TextNode, PathNode, FrameNode, RasterNode, AdjustmentNode]);
/** Nodes with children. */
export type ContainerNode = GroupNode | FrameNode;
export const isContainerNode = (n: Node | undefined): n is ContainerNode => n?.type === "group" || n?.type === "frame";
export type Node = z.infer<typeof Node>;
export type NodeType = Node["type"];
export type RectNode = z.infer<typeof RectNode>;
export type EllipseNode = z.infer<typeof EllipseNode>;
export type PolygonNode = z.infer<typeof PolygonNode>;
export type StarNode = z.infer<typeof StarNode>;
export type LineNode = z.infer<typeof LineNode>;
export type ImageNode = z.infer<typeof ImageNode>;
export type GroupNode = z.infer<typeof GroupNode>;
export type ShapeNode = RectNode | EllipseNode | PolygonNode | StarNode | PathNode;
export type NodeOf<T extends NodeType> = Extract<Node, { type: T }>;

/**
 * A saved pixel selection: 8-bit coverage over the page, `width × height` (stretched to the
 * page when loaded), deflate-compressed and base64-encoded.
 */
export const SavedChannel = z.object({
  id: Id,
  name: z.string().max(100),
  width: z.number().int().min(1).max(16384),
  height: z.number().int().min(1).max(16384),
  data: z.string().max(1_400_000),
});
export type SavedChannel = z.infer<typeof SavedChannel>;

export const Page = z.object({
  id: Id,
  name: z.string().max(200),
  width: z.number().min(1).max(100_000),
  height: z.number().min(1).max(100_000),
  background: Paint.nullable(),
  children: z.array(Id),
  /** Ruler guides (page units). */
  guides: z.array(z.object({ axis: z.enum(["x", "y"]), pos: z.number() })).max(200),
  /** Saved pixel selections ("Save selection"), optional so older documents stay valid. */
  channels: z.array(SavedChannel).max(16).optional(),
  /** Hidden pages stay in the editor (dimmed) but are left out of "all pages" exports. */
  hidden: z.boolean().optional(),
  /** Section this page belongs to (groups pages in the grid; consecutive pages share one). */
  section: z.string().max(80).optional(),
});
export type Page = z.infer<typeof Page>;

/** Content-addressed: id is the SHA-256 of the original file. */
export const Asset = z.object({
  id: z.string().regex(/^[0-9a-f]{64}$/),
  mime: z.enum(["image/png", "image/jpeg", "image/webp", "image/avif", "image/gif"]),
  width: z.int().positive(),
  height: z.int().positive(),
  bytes: z.int().nonnegative(),
  name: z.string().max(255),
});
export type Asset = z.infer<typeof Asset>;

export const Document = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: Id,
  name: z.string().max(200),
  pages: z.array(Page).min(1).max(500),
  nodes: z.record(Id, Node),
  assets: z.record(z.string(), Asset),
});
export type Document = z.infer<typeof Document>;

export interface IntegrityError {
  path: string;
  message: string;
}

/**
 * Structural checks Zod can't express: every node reachable exactly once from a page,
 * parentId matches the container, no cycles, image assets exist.
 */
export function checkIntegrity(doc: Document): IntegrityError[] {
  const errors: IntegrityError[] = [];
  const seen = new Set<string>();
  const visit = (ids: string[], parentId: string, path: string, depth: number) => {
    if (depth > 64) {
      errors.push({ path, message: "nesting too deep" });
      return;
    }
    ids.forEach((id, i) => {
      const at = `${path}[${i}]`;
      const node = doc.nodes[id];
      if (!node) return errors.push({ path: at, message: `missing node ${id}` });
      if (seen.has(id)) return errors.push({ path: at, message: `node ${id} referenced twice` });
      seen.add(id);
      if (node.id !== id) errors.push({ path: at, message: `node key/id mismatch ${id}` });
      if (node.parentId !== parentId) errors.push({ path: at, message: `node ${id} parentId ${node.parentId} != ${parentId}` });
      if (node.type === "image" && !doc.assets[node.assetId]) errors.push({ path: at, message: `missing asset ${node.assetId}` });
      if (node.type === "image" && node.originalAssetId && !doc.assets[node.originalAssetId]) errors.push({ path: at, message: `missing asset ${node.originalAssetId}` });
      if (node.type === "image")
        for (const [what, ref] of [["paint", node.paintTiles], ["retouch", node.retouchTiles]] as const) {
          if (isLocalRasterRef(ref ?? null)) errors.push({ path: at, message: `${what} of ${id} has unsaved pixels (${ref})` });
          else if (ref && !doc.assets[ref]) errors.push({ path: at, message: `missing ${what} tiles ${ref}` });
        }
      if (node.type === "raster") {
        if (isLocalRasterRef(node.tiles)) errors.push({ path: at, message: `raster ${id} has unsaved pixels (${node.tiles})` });
        else if (node.tiles && !doc.assets[node.tiles]) errors.push({ path: at, message: `missing raster tiles ${node.tiles}` });
        if (node.pixelWidth * node.pixelHeight > 100_000_000) errors.push({ path: at, message: `raster ${id} is over 100 megapixels` });
        const src = node.rasterizedFrom;
        for (const a of src ? [src.assetId, ...(src.originalAssetId ? [src.originalAssetId] : [])] : []) if (!doc.assets[a]) errors.push({ path: at, message: `missing asset ${a}` });
      }
      const mr = node.mask?.raster;
      if (mr) {
        if (isLocalRasterRef(mr.tiles)) errors.push({ path: at, message: `mask of ${id} has unsaved pixels (${mr.tiles})` });
        else if (mr.tiles && !doc.assets[mr.tiles]) errors.push({ path: at, message: `missing mask tiles ${mr.tiles}` });
        if (mr.pixelWidth * mr.pixelHeight > 100_000_000) errors.push({ path: at, message: `mask of ${id} is over 100 megapixels` });
      }
      if (node.mask?.vector?.type === "node" && node.mask.vector.nodeId === id) errors.push({ path: at, message: `node ${id} masks itself` });
      for (const e of node.effects) if (e.type === "patternOverlay" && !doc.assets[e.assetId]) errors.push({ path: at, message: `missing pattern asset ${e.assetId}` });
      if (node.type === "group" || node.type === "frame") visit(node.children, id, `${at}.children`, depth + 1);
    });
  };
  const pageIds = new Set<string>();
  doc.pages.forEach((p, i) => {
    if (pageIds.has(p.id)) errors.push({ path: `pages[${i}]`, message: `duplicate page ${p.id}` });
    pageIds.add(p.id);
    visit(p.children, p.id, `pages[${i}].children`, 0);
  });
  for (const id of Object.keys(doc.nodes)) if (!seen.has(id)) errors.push({ path: `nodes.${id}`, message: "orphan node" });
  for (const [k, a] of Object.entries(doc.assets)) if (a.id !== k) errors.push({ path: `assets.${k}`, message: "asset key/id mismatch" });
  return errors;
}
